import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, VStack, HStack, Button, Text, Badge, Alert, AlertIcon, useToast,
  useColorModeValue, Icon, Divider, Heading, Spinner, Center, Code,
  Input, InputGroup, InputRightElement, FormControl, FormLabel, Grid,
  Progress, Switch,
} from '@chakra-ui/react';
import { FiLink, FiCheckCircle, FiXCircle, FiRefreshCw, FiTrash2, FiEye, FiEyeOff, FiDownloadCloud, FiZap } from 'react-icons/fi';
import { doc, onSnapshot, updateDoc, deleteField } from 'firebase/firestore';
import { db, app } from '../../../utils/init-firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AriveSyncUserPanel from '../Arive/AriveSyncUserPanel';

function MaskedInput({ label, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);

  return (
    <FormControl>
      <FormLabel fontSize="xs" fontWeight="semibold" mb={1}>{label}</FormLabel>
      <InputGroup size="sm">
        <Input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          fontFamily="mono"
          fontSize="xs"
        />
        <InputRightElement>
          <Icon
            as={show ? FiEyeOff : FiEye}
            cursor="pointer"
            color="gray.400"
            onClick={() => setShow(!show)}
            boxSize={3.5}
          />
        </InputRightElement>
      </InputGroup>
    </FormControl>
  );
}

export default function AriveSettings({ userId, isAdmin = false }) {
  const toast = useToast();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [clearing, setClearing] = useState(false);

  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [dirty, setDirty] = useState(false);
  const [importing, setImporting] = useState(false);

  const [syncSettings, setSyncSettings]   = useState(null);
  const [syncLoading, setSyncLoading]     = useState(false);
  const [savingSetting, setSavingSetting] = useState(null);

  const successBG = useColorModeValue('green.50', 'green.900');
  const infoBG = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBG = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (!userId) return;
    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        setUserData(docSnap.data());
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [userId]);

  const isEnabled = userData?.ariveEnabled === true;
  const hasCredentials = !!(userData?.ariveCredentials?.clientId);
  const lastTested = userData?.ariveLastTestedAt;

  const loadSyncSettings = useCallback(async () => {
    if (!userId) return;
    setSyncLoading(true);
    try {
      const fns = getFunctions(app, 'us-east1');
      const fn = httpsCallable(fns, 'getAriveSyncUserSettings');
      const res = await fn({ targetUserId: userId });
      setSyncSettings(res.data?.settings || null);
    } catch (_) {
      setSyncSettings(null);
    } finally {
      setSyncLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isEnabled) loadSyncSettings();
  }, [isEnabled, loadSyncSettings]);

  const updateSyncSetting = async (field, value) => {
    setSavingSetting(field);
    try {
      const fns = getFunctions(app, 'us-east1');
      const fn = httpsCallable(fns, 'updateAriveSyncUserSettings');
      const res = await fn({ targetUserId: userId, [field]: value });
      setSyncSettings(res.data?.settings || null);
      toast({ title: 'Saved', status: 'success', duration: 1500 });
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSavingSetting(null);
    }
  };

  function maskValue(val) {
    if (!val) return '';
    if (val.length <= 4) return '****';
    return '*'.repeat(val.length - 4) + val.slice(-4);
  }

  const handleSaveAndTest = async () => {
    if (!clientId || !secret || !apiKey) {
      toast({ title: 'All three fields are required', status: 'warning', duration: 3000 });
      return;
    }

    setSaving(true);
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ariveCredentials: { clientId, secret, apiKey },
      });

      setDirty(false);
      toast({ title: 'Credentials saved', status: 'info', duration: 2000 });

      setTesting(true);
      const functions = getFunctions(app, 'us-east1');
      const testFn = httpsCallable(functions, 'testAriveConnection');
      const result = await testFn({ targetUserId: userId });

      if (result.data?.success) {
        toast({
          title: 'Arive connected!',
          description: `Found ${result.data.loanCount} loan(s) in your pipeline`,
          status: 'success',
          duration: 5000,
        });
        setClientId('');
        setSecret('');
        setApiKey('');
      }
    } catch (error) {
      toast({
        title: 'Connection test failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
      setTesting(false);
    }
  };

  const handleDisable = async () => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { ariveEnabled: false });
      toast({ title: 'Arive integration disabled', status: 'info', duration: 3000 });
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 3000 });
    }
  };

  const handleEnable = async () => {
    if (!hasCredentials) {
      toast({ title: 'Save credentials first', status: 'warning', duration: 3000 });
      return;
    }
    setTesting(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const testFn = httpsCallable(functions, 'testAriveConnection');
      const result = await testFn({ targetUserId: userId });
      if (result.data?.success) {
        toast({ title: 'Arive re-enabled!', status: 'success', duration: 3000 });
      }
    } catch (error) {
      toast({ title: 'Connection test failed', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setTesting(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all Arive credentials? This will disable the integration.')) return;
    setClearing(true);
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ariveCredentials: deleteField(),
        ariveEnabled: false,
        ariveLastTestedAt: deleteField(),
      });
      setClientId('');
      setSecret('');
      setApiKey('');
      toast({ title: 'Credentials cleared', status: 'info', duration: 3000 });
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 3000 });
    } finally {
      setClearing(false);
    }
  };

  const handleRetest = async () => {
    setTesting(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const testFn = httpsCallable(functions, 'testAriveConnection');
      const result = await testFn({ targetUserId: userId });
      if (result.data?.success) {
        toast({
          title: 'Connection verified',
          description: `${result.data.loanCount} loan(s) accessible`,
          status: 'success',
          duration: 4000,
        });
      }
    } catch (error) {
      toast({ title: 'Test failed', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <Center py={8}><Spinner /></Center>;
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="md" mb={2}>Arive LOS Integration</Heading>
        <Text fontSize="sm" color="gray.600">
          Connect your Arive account to sync loan data, stages, and borrower info with Origin CRM
        </Text>
      </Box>

      <Divider />

      {/* Connection Status */}
      <Box
        p={5}
        bg={isEnabled ? successBG : infoBG}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={isEnabled ? 'green.200' : borderColor}
      >
        <HStack justify="space-between" mb={3}>
          <HStack spacing={3}>
            <Icon as={FiLink} boxSize={6} color={isEnabled ? 'green.600' : 'gray.400'} />
            <VStack align="flex-start" spacing={0}>
              <Text fontWeight="semibold">Arive Connection</Text>
              {hasCredentials && (
                <Text fontSize="xs" color="gray.600">
                  Client ID: <Code fontSize="xs">{maskValue(userData?.ariveCredentials?.clientId)}</Code>
                </Text>
              )}
            </VStack>
          </HStack>

          {isEnabled ? (
            <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
              <HStack spacing={1}><Icon as={FiCheckCircle} boxSize={3} /><Text>Connected</Text></HStack>
            </Badge>
          ) : hasCredentials ? (
            <Badge colorScheme="yellow" fontSize="sm" px={3} py={1}>
              <HStack spacing={1}><Icon as={FiXCircle} boxSize={3} /><Text>Disabled</Text></HStack>
            </Badge>
          ) : (
            <Badge colorScheme="gray" fontSize="sm" px={3} py={1}>
              <HStack spacing={1}><Icon as={FiXCircle} boxSize={3} /><Text>Not Configured</Text></HStack>
            </Badge>
          )}
        </HStack>

        {isEnabled && lastTested && (
          <Box p={3} bg="white" borderRadius="md" borderWidth="1px" borderColor="green.200" mb={3}>
            <Grid templateColumns="repeat(2, 1fr)" gap={3} fontSize="sm">
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.600">Status</Text>
                <Text color="green.600">Active</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.600">Last Verified</Text>
                <Text fontSize="xs">
                  {lastTested?.toDate ? lastTested.toDate().toLocaleString() : new Date(lastTested).toLocaleString()}
                </Text>
              </Box>
            </Grid>
          </Box>
        )}

        <HStack spacing={2} flexWrap="wrap">
          {isEnabled && (
            <>
              <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={handleRetest} isLoading={testing}>
                Re-test Connection
              </Button>
              <Button size="sm" leftIcon={<FiXCircle />} colorScheme="red" variant="outline" onClick={handleDisable}>
                Disable
              </Button>
            </>
          )}
          {!isEnabled && hasCredentials && (
            <Button size="sm" leftIcon={<FiCheckCircle />} colorScheme="green" onClick={handleEnable} isLoading={testing}>
              Re-enable
            </Button>
          )}
          {hasCredentials && (
            <Button size="sm" leftIcon={<FiTrash2 />} colorScheme="red" variant="ghost" onClick={handleClear} isLoading={clearing}>
              Clear Credentials
            </Button>
          )}
        </HStack>
      </Box>

      {/* Auto-Sync Preferences (Phase 2: master switches) */}
      {isEnabled && (
        <Box p={5} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <HStack mb={4} spacing={2}>
            <Icon as={FiZap} color="purple.500" />
            <Heading size="sm">Auto-Sync Preferences</Heading>
            {syncLoading && <Spinner size="xs" />}
          </HStack>
          <Text fontSize="xs" color="gray.500" mb={4}>
            Master switches for the Origin &harr; Arive auto-sync rule engine.
            Detailed rule configuration is managed in <strong>Admin &raquo; Arive Sync Rules</strong>.
          </Text>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" p={3} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="sm" fontWeight="semibold">Outbound auto-sync (Origin &rarr; Arive)</Text>
                <Text fontSize="xs" color="gray.500">
                  When ON, rules can automatically create or update Arive entities based on your Origin deal changes.
                </Text>
              </VStack>
              <Switch
                size="md"
                isChecked={syncSettings?.outbound_auto_sync !== false}
                isDisabled={savingSetting === 'outbound_auto_sync'}
                onChange={(e) => updateSyncSetting('outbound_auto_sync', e.target.checked)}
              />
            </HStack>
            <HStack justify="space-between" p={3} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="sm" fontWeight="semibold">Inbound auto-create (Arive &rarr; Origin)</Text>
                <Text fontSize="xs" color="gray.500">
                  When ON, new loans/leads created in Arive (with you as originator) will be auto-imported as Origin deals.
                </Text>
              </VStack>
              <Switch
                size="md"
                isChecked={syncSettings?.inbound_auto_create === true}
                isDisabled={savingSetting === 'inbound_auto_create'}
                onChange={(e) => updateSyncSetting('inbound_auto_create', e.target.checked)}
              />
            </HStack>
          </VStack>
        </Box>
      )}

      {/* Phase 3: Per-user rule UI */}
      {isEnabled && <AriveSyncUserPanel userId={userId} />}

      {/* Credential Input */}
      <Box p={5} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
        <Heading size="sm" mb={4}>{hasCredentials ? 'Update Credentials' : 'Enter Your Arive API Keys'}</Heading>
        <Text fontSize="xs" color="gray.500" mb={4}>
          Get these from your Arive account settings. Each user needs their own keys -- access is scoped to loans in your pipeline.
        </Text>

        <VStack spacing={3}>
          <MaskedInput label="Client ID" value={clientId} onChange={(v) => { setClientId(v); setDirty(true); }} placeholder="Enter Client ID" />
          <MaskedInput label="Secret" value={secret} onChange={(v) => { setSecret(v); setDirty(true); }} placeholder="Enter Secret" />
          <MaskedInput label="API Key" value={apiKey} onChange={(v) => { setApiKey(v); setDirty(true); }} placeholder="Enter API Key" />

          <Button
            colorScheme="blue"
            leftIcon={<FiCheckCircle />}
            onClick={handleSaveAndTest}
            isLoading={saving || testing}
            loadingText={testing ? 'Testing...' : 'Saving...'}
            isDisabled={!clientId || !secret || !apiKey}
            w="100%"
            mt={2}
          >
            Save & Test Connection
          </Button>
        </VStack>
      </Box>

      {/* Pipeline Import */}
      {isEnabled && (
        <Box p={5} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <HStack justify="space-between" mb={3}>
            <VStack align="flex-start" spacing={0}>
              <Heading size="sm">Import Pipeline</Heading>
              <Text fontSize="xs" color="gray.500">
                Pull all loans and leads from your Arive account into Origin
              </Text>
            </VStack>
            <Button
              size="sm"
              colorScheme="purple"
              leftIcon={<FiDownloadCloud />}
              onClick={async () => {
                setImporting(true);
                try {
                  const functions = getFunctions(app, 'us-east1');
                  const fn = httpsCallable(functions, 'importAriveUserPipeline');
                  const result = await fn({ targetUserId: userId, enrichDetails: true });
                  toast({
                    title: 'Pipeline imported!',
                    description: `${result.data.loansFound} loans, ${result.data.leadsFound} leads found. ${result.data.enriched} enriched with full details.`,
                    status: 'success',
                    duration: 8000,
                  });
                } catch (err) {
                  toast({ title: 'Import failed', description: err.message, status: 'error', duration: 5000 });
                } finally {
                  setImporting(false);
                }
              }}
              isLoading={importing || userData?.ariveImportProgress?.status === 'discovering' || userData?.ariveImportProgress?.status === 'enriching'}
              loadingText={
                userData?.ariveImportProgress?.status === 'enriching'
                  ? `Enriching ${userData?.ariveImportProgress?.enriched || 0}...`
                  : userData?.ariveImportProgress?.status === 'discovering'
                    ? `Found ${userData?.ariveImportProgress?.loansFound || 0} loans...`
                    : 'Importing...'
              }
            >
              Import My Pipeline
            </Button>
          </HStack>

          {/* Live progress */}
          {userData?.ariveImportProgress && ['discovering', 'enriching'].includes(userData.ariveImportProgress.status) && (
            <VStack align="stretch" spacing={2}>
              <Progress size="sm" isIndeterminate colorScheme="purple" borderRadius="full" />
              <Text fontSize="xs" color="gray.500">
                {userData.ariveImportProgress.status === 'discovering'
                  ? `Discovering pipeline... ${userData.ariveImportProgress.loansFound || 0} loans, ${userData.ariveImportProgress.leadsFound || 0} leads`
                  : `Enriching loan details... ${userData.ariveImportProgress.enriched || 0} of ${Math.min(100, userData.ariveImportProgress.loansFound || 0)}`}
              </Text>
            </VStack>
          )}

          {/* Last import stats */}
          {userData?.ariveLastPipelineImportAt && (
            <HStack spacing={4} mt={2} flexWrap="wrap">
              <Text fontSize="xs" color="gray.500">
                Last imported: {userData.ariveLastPipelineImportAt?.toDate
                  ? userData.ariveLastPipelineImportAt.toDate().toLocaleString()
                  : new Date(userData.ariveLastPipelineImportAt).toLocaleString()}
              </Text>
              {userData?.arivePipelineStats && (
                <Badge colorScheme="purple" fontSize="10px">
                  {userData.arivePipelineStats.loans || 0} loans, {userData.arivePipelineStats.leads || 0} leads
                </Badge>
              )}
            </HStack>
          )}
        </Box>
      )}

      {/* Info */}
      <Alert status="info" borderRadius="md" fontSize="xs">
        <AlertIcon />
        <VStack align="flex-start" spacing={1}>
          <Text fontWeight="semibold">How it works</Text>
          <Text>Your Arive API keys are stored securely and used server-side only. You'll have access to all loans in your Arive pipeline -- the same loans visible to you in the Arive LOS.</Text>
        </VStack>
      </Alert>
    </VStack>
  );
}
